import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingConstantiaPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Constantia Estate Painters - Luxury Residential Painting | Heritage Property Specialists',
    description: 'Luxury painting services in Constantia, Bishopscourt, and Tokai. Specialized in estate properties and heritage homes. Over 18 years serving the Constantia Valley with premium craftsmanship.',
    keywords: 'painters Constantia, estate painters Constantia, luxury painting, heritage painting, residential painting, Constantia Valley painters',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Constantia',
          description: 'Luxury estate and heritage property painting',
          address: 'Constantia, Cape Town',
          priceRange: '$$$',
          serviceArea: ['Constantia', 'Bishopscourt', 'Tokai', 'Bergvliet', 'Wynberg']
        }),
        generateServiceSchema({
          name: 'Estate & Heritage Painting',
          description: 'Luxury residential and heritage property painting',
          provider: 'Painters Constantia',
          areaServed: ['Constantia', 'Bishopscourt', 'Tokai'],
          serviceType: ['Estate Painting', 'Heritage Restoration', 'Luxury Residential']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Constantia Estate Painters",
    location: "Constantia, Cape Town",
    website: "https://paintersconstantia.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/f9a2389d13b9af8fd6b7825341c3a602.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20wine%20estate%20exterior%20with%20white%20walls%20Cape%20Dutch%20architecture%20vineyards%20in%20Constantia%20professional%20finish%20with%20mountain%20views&width=800&height=600&seq=constantiagallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20in%20progress%20with%20professional%20painter%20applying%20premium%20paint%20to%20walls%20using%20roller%20clean%20workspace%20estate%20home%20setting&width=800&height=600&seq=constantiagallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=heritage%20wine%20farm%20building%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20historic%20structure%20Cape%20Dutch%20gables&width=800&height=600&seq=constantiagallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20estate%20interior%20living%20room%20with%20high%20ceilings%20elegant%20color%20scheme%20antique%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=constantiagallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Estate Painting",
        description: "Transform your Constantia estate with our expert painting services. We specialize in large properties, wine farms, and luxury homes that require comprehensive painting solutions.",
        features: ["Interior walls & ceilings", "Exterior facades & architectural details", "Outbuildings & guest cottages", "Premium color consultation"]
      },
      {
        title: "Wine Farm Painting",
        description: "Specialized painting services for wine farms and agricultural properties in Constantia. We understand the unique requirements of working wine estates and historic farm buildings.",
        features: ["Cellar & tasting room painting", "Historic farm building restoration", "Agricultural structure coating", "Weather-resistant finishes"]
      },
      {
        title: "Heritage Properties",
        description: "Expert restoration and painting services for heritage properties and Cape Dutch architecture. We use traditional techniques and period-appropriate materials to preserve historical integrity.",
        features: ["Cape Dutch gable restoration", "Heritage color matching", "Traditional lime wash techniques", "Architectural detail preservation"]
      }
    ],
    serviceAreas: ["Constantia", "Constantia Nek", "Tokai", "Bergvliet", "Bishopscourt", "Wynberg"],
    about: "Constantia Estate Painters is the leading painting company serving Constantia's prestigious wine estates and luxury properties. With over 17 years of specialized experience in estate and heritage property painting, we have established ourselves as the most trusted painting contractor for Constantia's most distinguished homes and wine farms. Our team of master craftsmen combines traditional techniques with modern expertise to deliver exceptional results.",
    aboutExtended: [
      "We specialize in estate painting, wine farm restoration, and heritage property conservation, offering comprehensive solutions that cater to the unique requirements of Constantia's exclusive properties. Our expertise extends across interior painting of grand estates, exterior painting of Cape Dutch architecture, wine cellar and tasting room finishes, heritage building restoration, and specialized protective coatings for agricultural structures.",
      "What sets us apart is our deep understanding of Constantia's architectural heritage and our commitment to using only premium paints and materials appropriate for historic and luxury properties. Our master painters are fully trained in both traditional restoration techniques and modern painting methods, and are fully insured, giving you complete confidence throughout your project.",
      "We understand that Constantia properties often represent generations of family heritage and significant investment. That's why we approach each project with the utmost respect and care, working closely with heritage consultants, architects, and property owners to ensure that every detail is perfect. Whether you're restoring a historic wine farm or maintaining a modern estate, we deliver exceptional results that honor the property's character while ensuring long-lasting protection and beauty."
    ],
    ourApproach: [
      "Comprehensive estate and heritage assessment",
      "Collaboration with heritage consultants",
      "Premium and period-appropriate materials",
      "Traditional and modern techniques combined",
      "Minimal disruption to estate operations",
      "Long-term maintenance planning"
    ],
    whyChoose: [
      "17+ years estate painting experience",
      "Wine farm specialists",
      "Heritage restoration experts",
      "Cape Dutch architecture knowledge",
      "Premium materials exclusively",
      "Excellence guaranteed"
    ],
    testimonials: [
      {
        name: "Richard Cloete",
        rating: 5,
        text: "Constantia Estate Painters did an outstanding job on our wine farm. They understood the importance of preserving the historic character while providing modern protection. The cellar and tasting room look magnificent!"
      },
      {
        name: "Sophia van der Merwe",
        rating: 5,
        text: "Exceptional service and craftsmanship. They restored our Cape Dutch homestead beautifully, respecting its heritage while ensuring it's protected for generations to come. Highly professional team."
      },
      {
        name: "Andrew Whitfield",
        rating: 5,
        text: "We've used Constantia Estate Painters for multiple projects on our property. Their knowledge of traditional techniques and attention to detail is impressive. They're our go-to painters for all estate work."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Constantia Estate Painters</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Constantia Estate Painters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in Cape Town's Southern Suburbs:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of estate and heritage painting services tailored to meet the unique needs of Constantia properties.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Constantia, Constantia Nek, Tokai</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: By Appointment</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Constantia</li>
                <li>Constantia Nek</li>
                <li>Tokai</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
